import { useEffect, useMemo, useState } from "react";
import { Badge, Button, Card } from "@vb/ui";
import type { BadgeKind } from "@vb/ui";
import { Seo } from "../lib/seo.js";
import { Section, SectionHeading } from "../components/Section.js";

const API_HOST: string =
  ((import.meta as unknown as { env: { VITE_API_HOST?: string } }).env.VITE_API_HOST ??
    "https://vaultbreaker-api.fly.dev").replace(/\/$/, "");

type ServiceStatus = "OPERATIONAL" | "DEGRADED" | "UNKNOWN";

interface ServiceCard {
  id: string;
  name: string;
  endpoint: string;
  note: string;
  status: ServiceStatus;
  lastChecked?: string;
}

const STATUS_TO_BADGE: Record<ServiceStatus, BadgeKind> = {
  OPERATIONAL: "win",
  DEGRADED: "bust",
  UNKNOWN: "fair",
};

const STATUS_TO_COLOR: Record<ServiceStatus, string> = {
  OPERATIONAL: "var(--green)",
  DEGRADED: "var(--red)",
  UNKNOWN: "var(--fg-3)",
};

const STATUS_LD = {
  "@context": "https://schema.org",
  "@type": "WebPage",
  name: "Vault Breaker Studios — System status",
  description:
    "Live status for the Vault Breaker marketing site, game server, and webhook delivery.",
  url: "https://vaultbreaker.dev/status",
};

export function Status() {
  const [gameServer, setGameServer] = useState<ServiceCard>({
    id: "game-server",
    name: "Game server (api.vaultbreaker.dev)",
    endpoint: API_HOST,
    note: `Fly.io · ${API_HOST}`,
    status: "UNKNOWN",
  });

  useEffect(() => {
    let cancelled = false;
    const controller = new AbortController();
    async function check(): Promise<void> {
      try {
        const res = await fetch(`${API_HOST}/health`, {
          method: "GET",
          signal: controller.signal,
        });
        if (cancelled) return;
        const checkedAt = new Date().toISOString();
        if (res.ok) {
          setGameServer((s) => ({ ...s, status: "OPERATIONAL", lastChecked: checkedAt }));
        } else {
          setGameServer((s) => ({ ...s, status: "DEGRADED", lastChecked: checkedAt }));
        }
      } catch {
        if (cancelled) return;
        setGameServer((s) => ({
          ...s,
          status: "UNKNOWN",
          lastChecked: new Date().toISOString(),
        }));
      }
    }
    void check();
    return () => {
      cancelled = true;
      controller.abort();
    };
  }, []);

  const services: ServiceCard[] = useMemo(
    () => [
      {
        id: "marketing",
        name: "Marketing site (vaultbreaker.dev)",
        endpoint: "https://vaultbreaker.dev",
        note: "Hosted on Vercel",
        status: "OPERATIONAL",
      },
      gameServer,
      {
        id: "webhooks",
        name: "Webhook delivery",
        endpoint: "—",
        note: "OPERATIONAL · last 24h: data unavailable on public surface",
        status: "OPERATIONAL",
      },
    ],
    [gameServer],
  );

  return (
    <>
      <Seo
        title="System status · Vault Breaker Studios"
        description="Live status for the Vault Breaker marketing site, game server, and webhook delivery."
        path="/status"
        jsonLd={STATUS_LD}
      />

      <Section>
        <Badge kind="fair" style={{ marginBottom: 18 }}>
          STATUS
        </Badge>
        <h1
          style={{
            fontFamily: "var(--font-display)",
            fontSize: "clamp(40px, 5.5vw, 64px)",
            lineHeight: 1,
            margin: 0,
            color: "var(--fg-1)",
            textTransform: "uppercase",
          }}
        >
          System status
        </h1>
        <p
          style={{
            fontSize: 17,
            color: "var(--fg-2)",
            marginTop: 14,
            maxWidth: 720,
            lineHeight: 1.55,
          }}
        >
          Live readout of the three surfaces operators rely on. The game-server check runs on
          every page load against{" "}
          <code className="mono" style={{ color: "var(--cyan)" }}>{`${API_HOST}/health`}</code>
          .
        </p>
      </Section>

      <Section style={{ paddingTop: 0 }}>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
            gap: 14,
          }}
        >
          {services.map((s) => (
            <ServiceCardView key={s.id} service={s} />
          ))}
        </div>
      </Section>

      <Section style={{ paddingTop: 0 }}>
        <SectionHeading
          eyebrow="Uptime"
          title="Last 30 days"
          lead="Placeholder — replaced with real data once Better Uptime or Statuspage is wired."
        />
        <Card style={{ padding: 22 }}>
          <UptimeBand />
          <div
            style={{
              marginTop: 14,
              display: "flex",
              justifyContent: "space-between",
              fontSize: 11,
              color: "var(--fg-3)",
              fontFamily: "var(--font-mono)",
            }}
          >
            <span>30 days ago</span>
            <span>today</span>
          </div>
        </Card>
      </Section>

      <Section style={{ paddingTop: 0 }}>
        <Card style={{ padding: 22, display: "grid", gap: 12 }}>
          <SectionHeading eyebrow="Incidents" title="Subscribe" />
          <p style={{ margin: 0, fontSize: 14, color: "var(--fg-2)", lineHeight: 1.55 }}>
            We post incident reports and post-mortems by email. Drop us a line and we'll add you
            to the operator notice list.
          </p>
          <a
            href="mailto:hello@vaultbreaker.dev?subject=Subscribe%20to%20incident%20reports"
            style={{ textDecoration: "none", justifySelf: "start" }}
          >
            <Button variant="gold" style={{ padding: "12px 18px", fontSize: 14 }}>
              SUBSCRIBE TO INCIDENTS →
            </Button>
          </a>
        </Card>
      </Section>
    </>
  );
}

function ServiceCardView({ service }: { service: ServiceCard }) {
  const dot = STATUS_TO_COLOR[service.status];
  return (
    <Card style={{ padding: 22, display: "grid", gap: 12 }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
        <h3
          style={{
            margin: 0,
            fontSize: 14,
            color: "var(--fg-1)",
            fontFamily: "var(--font-ui)",
            letterSpacing: "0.02em",
          }}
        >
          {service.name}
        </h3>
        <Badge kind={STATUS_TO_BADGE[service.status]}>{service.status}</Badge>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: 999,
            background: dot,
            flexShrink: 0,
            boxShadow: `0 0 8px ${dot}`,
          }}
        />
        <span style={{ fontSize: 13, color: "var(--fg-2)", lineHeight: 1.5 }}>{service.note}</span>
      </div>
      {service.lastChecked ? (
        <span
          style={{
            fontSize: 11,
            color: "var(--fg-3)",
            fontFamily: "var(--font-mono)",
          }}
        >
          Last checked: {new Date(service.lastChecked).toLocaleTimeString()}
        </span>
      ) : null}
    </Card>
  );
}

/** Renders 30 small squares as a placeholder uptime band. All green for now —
 *  wired to real data once a status provider (Better Uptime, Statuspage,
 *  Atlassian, etc.) is integrated. */
function UptimeBand() {
  // TODO: integrate real uptime data once Better Uptime or Statuspage is wired
  const days = Array.from({ length: 30 }, (_, i) => ({ id: i, ok: true }));
  return (
    <div
      role="img"
      aria-label="30-day uptime band, all operational"
      style={{
        display: "grid",
        gridTemplateColumns: "repeat(30, 1fr)",
        gap: 4,
      }}
    >
      {days.map((d) => (
        <span
          key={d.id}
          title={`Day -${30 - d.id}`}
          style={{
            display: "block",
            height: 24,
            borderRadius: 2,
            background: d.ok ? "var(--green)" : "var(--red)",
            opacity: 0.78,
          }}
        />
      ))}
    </div>
  );
}
